/**
 * Client-side mirror of the checks in ManualIngestionRequest
 * (server/pipeline_ingestion/serializers.py).
 *
 * Only the cheap, unambiguous rules live here — required fields, enum membership,
 * lengths, URL and date shape. The prose check (`assess_text_quality`) is
 * deliberately NOT reimplemented: two copies of that heuristic would drift, and a row
 * that passes here can still be refused server-side, which the result step reports
 * per row. The point of this pass is to keep an obviously broken spreadsheet from
 * being submitted at all, not to be the authority on what is accepted.
 */

import { ContentColumn } from "./parseContentCsv";

/** Mirrors the `content_type` ChoiceField server-side. */
export const CONTENT_TYPES = [
  "youtube",
  "book",
  "blog",
  "post",
  "podcast",
  "article",
] as const;

export const MAX_TITLE_LENGTH = 500;
export const MAX_AUTHOR_LENGTH = 200;
export const MIN_TEXT_LENGTH = 50;

export interface RowIssue {
  field: ContentColumn;
  message: string;
}

/** Year first: 2026-01-15 or 2026/1/15. Unambiguous, so read in ISO order. */
const YEAR_FIRST = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;

/** Year last: 01/15/2026, 1-15-2026. Read month-first — see normalisePublishDate. */
const YEAR_LAST = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Format as YYYY-MM-DD, or null if those numbers are not a real calendar date —
 * so "2026-02-31" is refused the way the server's DateField would refuse it.
 */
function toIsoDate(year: string, month: string, day: string): string | null {
  const [y, m, d] = [Number(year), Number(month), Number(day)];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (
    date.getUTCFullYear() !== y ||
    date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== d
  ) {
    return null;
  }
  return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Read a publish date written the way spreadsheets export it, and return it as
 * YYYY-MM-DD — the only form the server's DateField accepts.
 *
 * Requiring ISO here made every date column Excel produces fail validation, which is
 * a formatting difference, not a mistake in the data. Slash and dash dates with the
 * year last are read month-first, matching Excel's default export; a first part above
 * 12 cannot be a month, so it is read as a day rather than rejected. Two-digit years
 * are refused outright — "01/02/26" has no reading that isn't a guess.
 *
 * Returns "" for a blank value and null when nothing sensible can be read.
 */
export function normalisePublishDate(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const yearFirst = YEAR_FIRST.exec(trimmed);
  if (yearFirst) {
    const [, year, month, day] = yearFirst;
    return toIsoDate(year, month, day);
  }

  const yearLast = YEAR_LAST.exec(trimmed);
  if (yearLast) {
    const [, first, second, year] = yearLast;
    const monthFirst = Number(first) <= 12;
    return toIsoDate(year, monthFirst ? first : second, monthFirst ? second : first);
  }

  return null;
}

/**
 * True when the written date is a real date but not already in the form the server
 * wants — i.e. the upload will silently reinterpret it. Used to say so in the preview.
 */
export function isRewrittenDate(value: string): boolean {
  const normalised = normalisePublishDate(value);
  return normalised !== null && normalised !== "" && normalised !== value.trim();
}

/**
 * Trim the fields the server treats as identifiers, leaving `text` alone apart from
 * its surrounding whitespace so pasted content keeps its internal formatting.
 */
export function normaliseRow(
  values: Record<ContentColumn, string>,
): Record<ContentColumn, string> {
  return {
    content_type: values.content_type.trim().toLowerCase(),
    title: values.title.trim(),
    author: values.author.trim(),
    text: values.text.trim(),
    source_url: values.source_url.trim(),
    publish_date: values.publish_date.trim(),
  };
}

export function validateContentRow(values: Record<ContentColumn, string>): RowIssue[] {
  const row = normaliseRow(values);
  const issues: RowIssue[] = [];

  if (!row.content_type) {
    issues.push({ field: "content_type", message: "Required." });
  } else if (!(CONTENT_TYPES as readonly string[]).includes(row.content_type)) {
    issues.push({
      field: "content_type",
      message: `"${row.content_type}" is not a content type. Use one of: ${CONTENT_TYPES.join(", ")}.`,
    });
  }

  if (!row.title) {
    issues.push({ field: "title", message: "Required." });
  } else if (row.title.length > MAX_TITLE_LENGTH) {
    issues.push({
      field: "title",
      message: `${row.title.length} characters; the maximum is ${MAX_TITLE_LENGTH}.`,
    });
  }

  if (!row.author) {
    issues.push({ field: "author", message: "Required." });
  } else if (row.author.length > MAX_AUTHOR_LENGTH) {
    issues.push({
      field: "author",
      message: `${row.author.length} characters; the maximum is ${MAX_AUTHOR_LENGTH}.`,
    });
  }

  if (!row.text) {
    issues.push({ field: "text", message: "Required." });
  } else if (row.text.length < MIN_TEXT_LENGTH) {
    issues.push({
      field: "text",
      message: `${row.text.length} characters; at least ${MIN_TEXT_LENGTH} are needed.`,
    });
  }

  if (row.source_url && !isHttpUrl(row.source_url)) {
    issues.push({ field: "source_url", message: "Must be a full http(s) URL." });
  }

  if (row.publish_date && normalisePublishDate(row.publish_date) === null) {
    issues.push({
      field: "publish_date",
      message: `"${row.publish_date}" is not a date. Use YYYY-MM-DD or MM/DD/YYYY, with a four-digit year.`,
    });
  }

  return issues;
}

/**
 * Build the JSON payload for one row, omitting optional fields that are blank.
 *
 * Blank optionals must be dropped rather than sent as "": `publish_date: ""` fails
 * DateField, and an empty `source_url` is meaningless.
 */
export function toIngestionPayload(
  values: Record<ContentColumn, string>,
): Record<string, string> {
  const row = normaliseRow(values);
  // Whatever the spreadsheet wrote, the server only parses ISO.
  const publishDate = normalisePublishDate(row.publish_date) || "";
  return {
    content_type: row.content_type,
    title: row.title,
    author: row.author,
    text: row.text,
    ...(row.source_url ? { source_url: row.source_url } : {}),
    ...(publishDate ? { publish_date: publishDate } : {}),
  };
}

/**
 * Rows that duplicate an earlier row's title + author, by row index.
 *
 * Ingestion has no uniqueness constraint, so re-uploading a file quietly creates a
 * second copy of everything. Flagging in-file repeats catches the common version of
 * that mistake at the point where it is still cheap to fix.
 */
export function findDuplicateIndexes(
  rows: { index: number; values: Record<ContentColumn, string> }[],
): Set<number> {
  const seen = new Set<string>();
  const duplicates = new Set<number>();

  rows.forEach((row) => {
    const normalised = normaliseRow(row.values);
    if (!normalised.title || !normalised.author) return;
    const key = `${normalised.title.toLowerCase()}\u001f${normalised.author.toLowerCase()}`;
    if (seen.has(key)) {
      duplicates.add(row.index);
      return;
    }
    seen.add(key);
  });

  return duplicates;
}
